import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, TrendingDown, Bot, RefreshCw, Shield, Zap, Lock, 
  X, ChevronRight, AlertTriangle, CheckCircle, Info, Activity,
  Droplets, Users, Wallet, Eye, AlertCircle, BarChart3, Clock
} from 'lucide-react';
import OHLCVChart from 'components/OHLCV';
import styles from './RugCheck.module.sass';

const RugCheckComponent = ({ tokenAddress = '0xbc8c7a31774c12be9e827db283e0cd1de7b283' }) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('1h');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  const tokenData = {
    address: tokenAddress,
    price: 89806.56,
    priceChange: 9.32,
    volume24h: 2604000,
    marketCap: 89806560,
    fdv: 89806560,
    liquidity: 2604000,
  };

  const [aiAnalysis, setAiAnalysis] = useState({
    score: 48,
    status: 'MODERATE',
    loading: true,
    severity: 'MEDIUM RISK',
    text: '',
    risks: []
  });

  const securityScoreData = {
    score: 69,
    status: 'Critical',
    icon: Shield,
    category: 'security',
    details: 'Security Score',
    subtitle: '2 issues',
    expanded: {
      contractVerified: false,
      ownershipRenounced: false,
      mintFunction: true,
      pausable: true,
      blacklistEnabled: true,
      proxyContract: false,
      findings: [
        { type: 'critical', text: 'Contract ownership not renounced' },
        { type: 'critical', text: 'Mint function enabled - unlimited tokens can be created' },
        { type: 'critical', text: 'Contract can pause all transfers' },
        { type: 'warning', text: 'Blacklist function can block specific addresses' },
        { type: 'warning', text: 'Contract source code not verified' }
      ]
    }
  };

  const holderGrowthData = {
    score: 42,
    status: 'Warning',
    icon: Users,
    category: 'market',
    details: 'Holder Growth',
    subtitle: '-12% last 24h',
    expanded: {
      totalHolders: 156,
      change24h: '-12%',
      change7d: '-34%',
      newHolders24h: 3,
      lostHolders24h: 21,
      avgHoldingPeriod: '0.8 days',
      findings: [
        { type: 'critical', text: 'Declining holder count (-12% in 24h)' },
        { type: 'warning', text: 'Only 156 total holders' },
        { type: 'warning', text: 'Average holding period under 1 day' },
        { type: 'info', text: 'Low new holder acquisition rate' }
      ]
    }
  };

  const buySellPressureData = {
    score: 18,
    status: 'Critical',
    icon: TrendingDown,
    category: 'market',
    details: 'Buy/Sell Pressure',
    subtitle: '75% sell pressure',
    expanded: {
      buyVolume24h: '$650,000',
      sellVolume24h: '$1,954,000',
      buyTxCount: 89,
      sellTxCount: 253,
      avgBuySize: '$7,303',
      avgSellSize: '$7,727',
      findings: [
        { type: 'critical', text: 'Heavy sell pressure (75% of volume)' },
        { type: 'warning', text: 'Sell transactions 3x higher than buys' },
        { type: 'info', text: 'Average sell size exceeds buy size' },
        { type: 'warning', text: 'Sustained selling over last 6 hours' }
      ]
    }
  };

  const liquidityHealthData = {
    score: 58,
    status: 'Critical',
    icon: Droplets,
    category: 'market',
    details: 'Liquidity Health',
    subtitle: '$2.6M unlocked',
    expanded: {
      totalLiquidity: '$2,604,000',
      lockedLiquidity: '$0',
      liquidityRatio: '2.9%',
      impactThreshold: '$5,000',
      pools: [
        { dex: 'Uniswap V2', pair: 'TOKEN/ETH', liquidity: '$2,604,000', locked: '0%' },
        { dex: 'Uniswap V3', pair: 'TOKEN/ETH', liquidity: '$0', locked: '0%' }
      ],
      findings: [
        { type: 'critical', text: 'No liquidity locked' },
        { type: 'critical', text: 'Low liquidity to market cap ratio (2.9%)' },
        { type: 'warning', text: '$5k trade causes 2%+ price impact' },
        { type: 'info', text: 'Single liquidity pool dependency' }
      ]
    }
  };

  const marketActivityData = {
    score: 35,
    status: 'Moderate',
    icon: Activity,
    category: 'market',
    details: 'Market Activity',
    subtitle: '342 txns today',
    expanded: {
      transactions24h: 342,
      uniqueTraders24h: 98,
      avgTxSize: '$7,602',
      volumeChange24h: '+156%',
      priceVolatility: '24.3%',
      largestTx24h: '$152,000',
      findings: [
        { type: 'warning', text: 'High price volatility (24.3%)' },
        { type: 'info', text: 'Volume increased 156% in 24h' },
        { type: 'warning', text: 'Large transaction detected ($152k)' },
        { type: 'info', text: 'Moderate unique trader count' }
      ]
    }
  };

  useEffect(() => {
    setTimeout(() => {
      const newScore = 48;
      const riskLevel = getRiskLevel(newScore);
      
      setAiAnalysis({
        score: newScore,
        status: riskLevel.status,
        severity: riskLevel.severity,
        loading: false,
        text: `This token presents multiple critical security risks that strongly indicate potential rug pull characteristics. Key concerns include: contract security failures with 3 critical issues, declining holder growth (-12% in 24h), extreme sell pressure at 75% of volume, unlocked liquidity of $2.6M that can be removed at any time, and high price volatility of 24.3%. The combination of poor security metrics and negative market indicators creates an extremely dangerous investment environment.`,
        risks: [
          { level: 'critical', text: 'Contract has 3 critical security issues' },
          { level: 'critical', text: 'Heavy sell pressure at 75% of volume' },
          { level: 'warning', text: 'Declining holder count (-12% in 24h)' },
          { level: 'critical', text: 'No liquidity locked ($2.6M at risk)' },
          { level: 'warning', text: 'High price volatility (24.3%)' }
        ]
      });
    }, 2000);
  }, []);

  const getScoreColor = (score) => {
    if (score >= 4) return styles.good;
    if (score >= 3) return styles.moderate;
    if (score >= 2) return styles.warning;
    return styles.critical;
  };

  const getColorStyles = (value) => {
    const score = (value / 100) * 5;
    if (score < 2) {
      return {
        progress: {
          background: '#f44336',
          boxShadow: '0 0 10px rgba(244, 67, 54, 0.4)'
        }
      };
    }
    if (score < 3.5) {
      return {
        progress: {
          background: '#9c27b0',
          boxShadow: '0 0 10px rgba(156, 39, 176, 0.4)'
        }
      };
    }
    return {
      progress: {
        background: '#73F6FF',
        boxShadow: '0 0 10px rgba(115, 246, 255, 0.3)'
      }
    };
  };

  const getRiskIcon = (score) => {
    if (score >= 80) return <CheckCircle size={14} />;
    if (score >= 40) return <AlertCircle size={14} />;
    return <AlertTriangle size={14} />;
  };

  const getRiskLevel = (score) => {
    if (score >= 80) {
      return {
        severity: 'MINIMAL RISK',
        status: 'SAFE',
        color: '#73F6FF',
        gradientStart: '#73F6FF',
        gradientEnd: '#3DD4E0',
        bgGradientStart: 'rgba(115, 246, 255, 0.12)',
        bgGradientEnd: 'rgba(115, 246, 255, 0.06)',
        borderColor: 'rgba(115, 246, 255, 0.15)',
        glowColor: 'rgba(115, 246, 255, 0.3)'
      };
    }
    if (score >= 60) {
      return {
        severity: 'LOW RISK',
        status: 'CAUTION',
        color: '#8B5CF6',
        gradientStart: '#8B5CF6',
        gradientEnd: '#7C3AED',
        bgGradientStart: 'rgba(139, 92, 246, 0.12)',
        bgGradientEnd: 'rgba(139, 92, 246, 0.06)',
        borderColor: 'rgba(139, 92, 246, 0.15)',
        glowColor: 'rgba(139, 92, 246, 0.3)'
      };
    }
    if (score >= 40) {
      return {
        severity: 'MEDIUM RISK',
        status: 'MODERATE',
        color: '#F59E0B',
        gradientStart: '#F59E0B',
        gradientEnd: '#D97706',
        bgGradientStart: 'rgba(245, 158, 11, 0.12)',
        bgGradientEnd: 'rgba(245, 158, 11, 0.06)',
        borderColor: 'rgba(245, 158, 11, 0.15)',
        glowColor: 'rgba(245, 158, 11, 0.3)'
      };
    }
    if (score >= 20) {
      return {
        severity: 'HIGH RISK',
        status: 'WARNING',
        color: '#EA580C',
        gradientStart: '#F97316',
        gradientEnd: '#DC2626',
        bgGradientStart: 'rgba(234, 88, 12, 0.12)',
        bgGradientEnd: 'rgba(220, 38, 38, 0.06)',
        borderColor: 'rgba(234, 88, 12, 0.15)',
        glowColor: 'rgba(234, 88, 12, 0.3)'
      };
    }
    return {
      severity: 'EXTREME RISK',
      status: 'CRITICAL',
      color: '#FF3D3D',
      gradientStart: '#FF6B6B',
      gradientEnd: '#FF3D3D',
      bgGradientStart: 'rgba(239, 68, 68, 0.12)',
      bgGradientEnd: 'rgba(239, 68, 68, 0.06)',
      borderColor: 'rgba(239, 68, 68, 0.15)',
      glowColor: 'rgba(239, 68, 68, 0.3)'
    };
  };

  const renderScoreSegments = (score) => {
    const scoreValue = (score / 100) * 5;
    const colorStyles = getColorStyles(score);
    
    return (
      <div className={styles.scoreSegments}>
        {[...Array(5)].map((_, i) => {
          const isFullyFilled = i < Math.floor(scoreValue);
          const isPartiallyFilled = i === Math.floor(scoreValue);
          const partialFillPercentage = isPartiallyFilled ? (scoreValue % 1) * 100 : 0;
          
          let segmentStyle = {};
          if (isFullyFilled) {
            segmentStyle = colorStyles.progress;
          } else if (isPartiallyFilled && partialFillPercentage > 0) {
            const color = colorStyles.progress.background;
            segmentStyle = {
              background: `linear-gradient(to right, ${color} ${partialFillPercentage}%, rgba(115, 246, 255, 0.1) ${partialFillPercentage}%)`,
              boxShadow: colorStyles.progress.boxShadow
            };
          }
          
          return (
            <div 
              key={i}
              className={`${styles.segment} ${isFullyFilled ? styles.segmentFilled : ''}`}
              style={segmentStyle}
            />
          );
        })}
      </div>
    );
  };

  const Modal = ({ title, data, onClose }) => (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>{title}</h3>
          <button className={styles.modalClose} onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div className={styles.modalBody}>
          {data}
        </div>
      </div>
    </div>
  );

  const renderModalContent = (data) => (
    <div className={styles.modalContainer}>
      <div className={styles.modalGrid}>
        {Object.entries(data.expanded).filter(([key]) => 
          !['findings', 'pools'].includes(key)
        ).map(([key, value]) => (
          <div key={key} className={styles.modalStat}>
            <div className={styles.modalStatLabel}>
              {key.replace(/([A-Z])/g, ' $1').trim()}
            </div>
            <div className={styles.modalStatValue}>{value}</div>
          </div>
        ))}
      </div>

      {data.expanded.pools && (
        <div className={styles.poolsSection}>
          <h4>Liquidity Pools</h4>
          <div className={styles.poolsTable}>
            <table>
              <thead>
                <tr>
                  <th>DEX</th>
                  <th>Pair</th>
                  <th>Liquidity</th>
                  <th>Locked</th>
                </tr>
              </thead>
              <tbody>
                {data.expanded.pools.map((pool, i) => (
                  <tr key={i}>
                    <td>{pool.dex}</td>
                    <td>{pool.pair}</td>
                    <td className={styles.monospace}>{pool.liquidity}</td>
                    <td>{pool.locked}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className={styles.findingsSection}>
        <h4>Security Findings</h4>
        <div className={styles.findingsList}>
          {data.expanded.findings.map((finding, i) => (
            <div key={i} className={`${styles.finding} ${styles[finding.type]}`}>
              <div className={styles.findingIcon}>
                {finding.type === 'critical' && <AlertTriangle size={14} />}
                {finding.type === 'warning' && <AlertCircle size={14} />}
                {finding.type === 'info' && <Info size={14} />}
              </div>
              <span>{finding.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className={styles.container}>
        <div className={styles.mainGrid}>
          <div className={styles.leftPanel}>
            <div className={styles.scoreCard}>
              <div className={styles.scoreHeader}>
                <div 
                  className={styles.scoreBadge}
                  style={{
                    background: `linear-gradient(135deg, ${getRiskLevel(aiAnalysis.score).bgGradientStart} 0%, ${getRiskLevel(aiAnalysis.score).bgGradientEnd} 100%)`,
                    border: `1px solid ${getRiskLevel(aiAnalysis.score).borderColor}`
                  }}
                >
                  <AlertTriangle 
                    size={20} 
                    style={{ color: getRiskLevel(aiAnalysis.score).color }}
                  />
                  <span style={{ color: getRiskLevel(aiAnalysis.score).color }}>
                    {aiAnalysis.severity}
                  </span>
                </div>
                <button 
                  className={`${styles.refreshBtn} ${isAnalyzing ? styles.spinning : ''}`}
                  onClick={() => setIsAnalyzing(true)}
                  title="Refresh Analysis"
                >
                  <RefreshCw size={14} />
                </button>
              </div>
              
              <div className={styles.scoreContentCompact}>
                <div className={styles.riskDisplay}>
                  <div className={styles.riskScore}>
                    <span 
                      className={styles.riskNumber}
                      style={{
                        background: `linear-gradient(135deg, ${getRiskLevel(aiAnalysis.score).gradientStart} 0%, ${getRiskLevel(aiAnalysis.score).gradientEnd} 100%)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        textShadow: `0 0 40px ${getRiskLevel(aiAnalysis.score).glowColor}`
                      }}
                    >
                      {aiAnalysis.score}
                    </span>
                    <span 
                      className={styles.riskMax}
                      style={{ color: `${getRiskLevel(aiAnalysis.score).color}66` }}
                    >
                      /100
                    </span>
                  </div>
                  <span className={styles.riskLabel}>Risk Score</span>
                  <div className={styles.riskBar}>
                    <div 
                      className={styles.riskBarFill} 
                      style={{ 
                        width: `${aiAnalysis.score}%`,
                        background: `linear-gradient(90deg, ${getRiskLevel(aiAnalysis.score).gradientEnd} 0%, ${getRiskLevel(aiAnalysis.score).gradientStart} 50%, ${getRiskLevel(aiAnalysis.score).gradientEnd} 100%)`,
                        boxShadow: `0 0 12px ${getRiskLevel(aiAnalysis.score).glowColor}`
                      }}
                    />
                  </div>
                </div>
                
                <div className={styles.statusDisplay}>
                  <div className={styles.statusGrid}>
                    <div className={styles.statusItem}>
                      <span className={styles.statusLabel}>Token Security</span>
                      <span 
                        className={styles.statusValue}
                        style={{
                          color: getRiskLevel(aiAnalysis.score).color,
                          background: `linear-gradient(135deg, ${getRiskLevel(aiAnalysis.score).bgGradientStart} 0%, ${getRiskLevel(aiAnalysis.score).bgGradientEnd} 100%)`,
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: `1px solid ${getRiskLevel(aiAnalysis.score).borderColor}`,
                          boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.2)',
                          textAlign: 'center'
                        }}
                      >
                        {aiAnalysis.status}
                      </span>
                    </div>
                    <div className={styles.statusDivider} />
                    <div className={styles.statusItem}>
                      <span className={styles.statusLabel}>Market Conditions</span>
                      <span 
                        className={styles.statusValue}
                        style={{
                          color: getRiskLevel(aiAnalysis.score).color,
                          background: `linear-gradient(135deg, ${getRiskLevel(aiAnalysis.score).bgGradientStart} 0%, ${getRiskLevel(aiAnalysis.score).bgGradientEnd} 100%)`,
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: `1px solid ${getRiskLevel(aiAnalysis.score).borderColor}`,
                          boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.2)',
                          textAlign: 'center'
                        }}
                      >
                       UNFAVORABLE 
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.metricsGrid}>
              <div className={styles.riskFactorsHeader}>
                <span>Risk Factors</span>
              </div>
              {[securityScoreData, holderGrowthData, buySellPressureData, liquidityHealthData, marketActivityData].map((metric, index) => {
                const Icon = metric.icon;
                
                // Determine score-based class
                let scoreClass = '';
                if (metric.score < 20) scoreClass = styles.scoreCritical;
                else if (metric.score < 40) scoreClass = styles.scoreWarning;
                else if (metric.score < 70) scoreClass = styles.scoreModerate;
                else scoreClass = styles.scoreGood;
                
                return (
                  <div 
                    key={index}
                    className={`${styles.metricCard} ${scoreClass}`}
                    onClick={() => setExpandedCard(metric.details)}
                  >
                    <div className={styles.metricHeader}>
                      <div className={styles.metricIcon}>
                        <Icon size={18} />
                      </div>
                      <div className={styles.metricInfo}>
                        <span className={styles.metricTitle}>{metric.details}</span>
                        <span className={styles.metricSubtitle}>{metric.subtitle}</span>
                      </div>
                    </div>
                    <div className={styles.metricScore}>
                      {renderScoreSegments(metric.score)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={styles.rightPanel}>
            <div className={styles.aiSectionCompact}>
              <div className={styles.aiHeader}>
                <div className={styles.aiTitle}>
                  <Bot size={16} />
                  <span>AI Security Assessment</span>
                </div>
                <div className={styles.aiStatus}>
                  {aiAnalysis.loading ? (
                    <span className={styles.analyzing}>
                      <Activity size={12} />
                      Analyzing
                    </span>
                  ) : (
                    <span className={styles.complete}>
                      <CheckCircle size={12} />
                      Complete
                    </span>
                  )}
                </div>
              </div>

              <div className={styles.aiContentCompact}>
                {aiAnalysis.loading ? (
                  <div className={styles.aiLoading}>
                    <div className={styles.loadingBar}>
                      <div className={styles.loadingProgress} />
                    </div>
                    <span>Scanning smart contract...</span>
                  </div>
                ) : (
                  <div className={styles.aiCompactGrid}>
                    <div className={styles.aiSummaryCompact}>
                      {aiAnalysis.text}
                    </div>

                    <div className={styles.aiMetricsCompact}>
                      <div className={styles.metricCompact}>
                        <span className={styles.metricNumber}>{aiAnalysis.risks.filter(r => r.level === 'critical').length}</span>
                        <span className={styles.metricText}>Critical</span>
                      </div>
                      <div className={styles.metricCompact}>
                        <span className={styles.metricNumber}>{aiAnalysis.risks.filter(r => r.level === 'warning').length}</span>
                        <span className={styles.metricText}>Warning</span>
                      </div>
                      <div className={styles.metricCompact}>
                        <span className={styles.metricNumber}>{aiAnalysis.score}</span>
                        <span className={styles.metricText}>Risk</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.chartSection}>
              <div className={styles.chartContent}>
                <OHLCVChart 
                  tokenAddress={tokenAddress}
                  title="LINK/USD"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {expandedCard === securityScoreData.details && (
        <Modal
          title="Security Analysis"
          onClose={() => setExpandedCard(null)}
          data={renderModalContent(securityScoreData)}
        />
      )}

      {expandedCard === holderGrowthData.details && (
        <Modal
          title="Holder Growth Analysis"
          onClose={() => setExpandedCard(null)}
          data={renderModalContent(holderGrowthData)}
        />
      )}

      {expandedCard === buySellPressureData.details && (
        <Modal
          title="Buy/Sell Pressure Analysis"
          onClose={() => setExpandedCard(null)}
          data={renderModalContent(buySellPressureData)}
        />
      )}

      {expandedCard === liquidityHealthData.details && (
        <Modal
          title="Liquidity Health Analysis"
          onClose={() => setExpandedCard(null)}
          data={renderModalContent(liquidityHealthData)}
        />
      )}

      {expandedCard === marketActivityData.details && (
        <Modal
          title="Market Activity Analysis"
          onClose={() => setExpandedCard(null)}
          data={renderModalContent(marketActivityData)}
        />
      )}
    </>
  );
};

export default RugCheckComponent;